import { match, P } from 'ts-pattern';

import type { SubscriptionMe } from '@/entities/subscription';
import { isCardExpired } from '@/shared/lib/format-utils';

import type { BillingStatus } from './get-billing-status';

// Two different facts, and the alert has to say which: a card that is already dead, and one
// that still works today but will not survive the next charge.
export type CardExpiryWarning = 'expired' | 'before-next-bill';

type CardExpiry = Pick<SubscriptionMe, 'cardExpMonth' | 'cardExpYear' | 'nextInvoiceAt'>;

// The warning is about the charge that will fail, so a card still good today is measured
// against the upcoming billing date rather than the day it is viewed.
const warningFor = (card: CardExpiry, nextChargeAt: Date | null): CardExpiryWarning | null => {
    if (isCardExpired(card.cardExpMonth, card.cardExpYear)) {
        return 'expired';
    }
    return nextChargeAt && isCardExpired(card.cardExpMonth, card.cardExpYear, nextChargeAt)
        ? 'before-next-bill'
        : null;
};

// Only where the card still matters: an inactive state's own warning already says what needs
// fixing, and a second alert on the same card would compete with it.
export const getCardExpiryWarning = (
    status: BillingStatus,
    card: CardExpiry,
): CardExpiryWarning | null =>
    match(status)
        .with('active', () =>
            warningFor(card, card.nextInvoiceAt ? new Date(card.nextInvoiceAt) : null),
        )
        // The provider stops previewing an invoice once a cancellation is scheduled, so the one
        // still on the subscription is stale and only the expired verdict is reachable.
        .with('cancelling', () => warningFor(card, null))
        .with(P.union('canceled', 'past-due'), () => null)
        .exhaustive();
